import {
  CcfAvailableIcon,
  CcfAvatar,
  CcfBox,
  CcfCloseIcon,
  CcfOfflineIcon,
  CcfSearchIcon,
  CcfTextField,
  CcfTypography,
  CcfUnavailableIcon,
  CcfWorkingIcon,
  useTranslator,
} from '@nice-ccf/ui-controls';
import styles from './conversations-new-message.module.scss';
import {
  activeAgentsList,
  agentsData,
} from '../mock-data/agent-conversations-mock-data';
import { useState } from 'react';
import { Autocomplete, Divider, InputAdornment } from '@mui/material';
import {
  ccfConversationsActions,
  getAgentMessagesAppSpaceView,
  getAgents,
} from '../+state/ccf-conversations.slice';
import { useDispatch, useSelector } from 'react-redux';
import { ArrowBackIosNew } from '@mui/icons-material';
import { getAppSpaceRatio } from '../../../global.app.slice';

/* eslint-disable-next-line */
export interface ConversationsNewMessageProps {}

/**
 * Component for starting a new conversation
 * @example - <ConversationsNewMessage />
 * @returns
 */
export function ConversationsNewMessage(props: ConversationsNewMessageProps) {
  const [translate] = useTranslator();
  const dispatch = useDispatch();
  const agents = useSelector(getAgents);
  const agentsList = activeAgentsList;
  const [searchTxt, setSearchTxt] = useState('');
  const [filteredAgentList, setFilteredAgentList] = useState<any[]>([]);
  const [selectedAgent, setSelectedAgent] = useState<any | null>(null);
  const {
    setNewAgentSelected,
    setAgentMessagesAppSpaceView,
    updateSelectedAgent,
    updateAgentGroups,
    updateAgents,
    setIsNewMessage,
  } = ccfConversationsActions;
  const agentMessagesAppSpaceView = useSelector(getAgentMessagesAppSpaceView);

  /**
   * Function to render status icon
   * @param e - any
   * @example renderStatusIcon(status)
   */
  function renderStatusIcon(status: string) {
    switch (status) {
      case 'working':
        return <CcfWorkingIcon className={styles.statusIcon} />;
      case 'available':
        return <CcfAvailableIcon className={styles.statusIcon} />;
      case 'busy':
        return <CcfUnavailableIcon className={styles.statusIcon} />;
      case 'offline':
        return (
          <CcfOfflineIcon
            className={styles.statusIcon}
            style={{ color: 'inherit' }}
          />
        );
      default:
        return <CcfAvailableIcon className={styles.statusIcon} />;
    }
  }

  /**
   * Handler for search agents across agent directory
   * @example searchAgentHandler()
   */
  const searchAgentHandler = (e: any) => {
    const searchText = e.target.value.toLowerCase();
    setSearchTxt(searchText);
    if (searchText) {
      const searchedAgents = agentsList.filter((agent) =>
        agent.name.toLowerCase().startsWith(searchText)
      );
      setFilteredAgentList(searchedAgents);
    } else {
      setFilteredAgentList([]);
    }
  };

  /**
   * Handler for selecting an agent from the list to start a conversation
   * @example addAgentHandler()
   */
  const selectAgentHandler = (agent: any) => {
    const otherAgents = agents.filter((agent: any) => !agent.isFavorite);
    const matchingAgent = otherAgents.find(
      (otherAgent: any) =>
        otherAgent.name.toLowerCase() === agent.name.toLowerCase()
    );
    if (matchingAgent !== null && matchingAgent !== undefined) {
      setSelectedAgent(matchingAgent);
      dispatch(updateSelectedAgent({ agent: matchingAgent, type: 'direct' }));
      const action = updateAgents;
      dispatch(action({ agent: matchingAgent, type: 'update' }));
      dispatch(setIsNewMessage(false));
    } else {
      setSelectedAgent(agent);
      dispatch(setNewAgentSelected(agent));
    }
    setSearchTxt('');
    setFilteredAgentList([]);
  };

  /**
   * Function to go back to agent list
   * @example backtoAgentList()
   */
  const backtoAgentList = () => {
    dispatch(setAgentMessagesAppSpaceView('agentsView'));
    setSelectedAgent(null);
  };

  return (
    <CcfBox className={styles.conversationsNewMessageContainer}>
      <CcfBox className={styles.conversationContainerHeader}>
        {agentMessagesAppSpaceView === 'messagesView' && (
          <ArrowBackIosNew
            onClick={backtoAgentList}
            style={{ fontSize: '14px', cursor: 'pointer' }}
          />
        )}
        {selectedAgent ? (
          <CcfBox
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              width: '100%',
            }}
          >
            <CcfBox className={styles.selectedAgentInfoBlock}>
              {renderStatusIcon(selectedAgent.status)}
              <CcfTypography className={styles.agentTitle}>
                {selectedAgent.name}
              </CcfTypography>
              <CcfTypography className={styles.agentSubTitle}>
                | {selectedAgent.status}
              </CcfTypography>
            </CcfBox>
            {agentMessagesAppSpaceView !== 'messagesView' && (
              <CcfBox style={{ display: 'flex', height: '100%' }}>
                <CcfCloseIcon
                  onClick={() => {
                    dispatch(
                      updateSelectedAgent({
                        agent: agentsData[0],
                        type: 'favourite',
                      })
                    );
                    dispatch(setIsNewMessage(false));
                  }}
                  style={{ fontSize: '1rem', pointerEvents: 'auto' }}
                />
              </CcfBox>
            )}
          </CcfBox>
        ) : (
          <CcfBox>
            <CcfTypography className={styles.newMessageLabel}>
              New Message
            </CcfTypography>
          </CcfBox>
        )}
      </CcfBox>

      {selectedAgent && <Divider />}

      {!selectedAgent && (
        <CcfBox>
          <Autocomplete
            freeSolo
            options={filteredAgentList}
            getOptionLabel={(option) => option.name}
            inputValue={searchTxt}
            value={null} // Set value to null to prevent showing the selected value
            onInputChange={(event, newInputValue) => {
              setSearchTxt(newInputValue);
              searchAgentHandler({ target: { value: newInputValue } });
              if (!newInputValue) {
                setFilteredAgentList([]); // Clear the filtered list when input is cleared
              }
            }}
            onChange={(event, value) => {
              if (value) {
                selectAgentHandler(value);
              }
            }}
            renderOption={(props, option, index) => (
              <li {...props} className={styles.customOptionContainer}>
                <CcfBox className={styles.optionBlock}>
                  <CcfAvatar
                    width={22}
                    height={22}
                    fontSize={12}
                    bgcolor="#009DE0"
                    borderColor="#009DE0"
                    border={true}
                  >
                    {option.name
                      .split(' ')
                      .map((n: string) => n[0])
                      .join('')}
                  </CcfAvatar>
                  <CcfTypography className={styles.agentNameTxt}>
                    {option.name}
                  </CcfTypography>
                </CcfBox>
                <CcfBox className={styles.optionBlock}>
                  {renderStatusIcon(option.status)}
                  <CcfTypography className={styles.agentStatusTxt}>
                    {option.status}
                  </CcfTypography>
                </CcfBox>
              </li>
            )}
            renderInput={(params) => (
              <CcfTextField
                {...params}
                size="small"
                style={{
                  background: 'white',
                }}
                className={styles.textField}
                InputProps={{
                  ...params.InputProps,
                  startAdornment: (
                    <InputAdornment position="start">
                      <CcfSearchIcon />
                    </InputAdornment>
                  ),
                  endAdornment: null,
                }}
                placeholder="Search"
                variant="outlined"
                fullWidth
              />
            )}
          />
        </CcfBox>
      )}
    </CcfBox>
  );
}

export default ConversationsNewMessage;
